// Pure badge, habit-level and canopy definitions shared by backend and both clients.

export type HabitLevel = {
  level: number
  name: string
  // Total check-ins at which the next level starts; null at max level.
  next: number | null
  // 0–1 progress through the current level.
  progress: number
}

// A habit grows from seed to ancient tree by its total check-ins.
export const HABIT_LEVELS = [
  { name: 'seed', at: 0 },
  { name: 'sprout', at: 3 },
  { name: 'seedling', at: 7 },
  { name: 'sapling', at: 14 },
  { name: 'youngTree', at: 30 },
  { name: 'tree', at: 60 },
  { name: 'oak', at: 100 },
  { name: 'elder', at: 180 },
  { name: 'ancient', at: 365 },
] as const

export const MAX_HABIT_LEVEL = HABIT_LEVELS.length
export const TREE_LEVEL = 6

export function habitLevel(total: number): HabitLevel {
  const n = Math.max(0, Math.floor(total))
  let i = 0
  while (i + 1 < HABIT_LEVELS.length && n >= HABIT_LEVELS[i + 1].at) i++
  const cur = HABIT_LEVELS[i]
  const nxt = HABIT_LEVELS[i + 1]
  return {
    level: i + 1,
    name: cur.name,
    next: nxt ? nxt.at : null,
    progress: nxt ? (n - cur.at) / (nxt.at - cur.at) : 1,
  }
}

export const BADGE_TIERS = ['seed', 'sprout', 'sapling', 'oak', 'ancient'] as const
export type BadgeTier = (typeof BADGE_TIERS)[number]

export const TIER_COLORS: Record<BadgeTier, string> = {
  seed: '#b8a27a',
  sprout: '#7ed957',
  sapling: '#2fb67c',
  oak: '#d08a2e',
  ancient: '#e7b416',
}

export type BadgeCategory = 'planning' | 'habits' | 'growth' | 'coaching' | 'social' | 'consistency'

// Snapshot of countable facts about a user. Absent = unknown (not computed in
// this pass), so a check never fails a badge just because a stat was skipped.
export type ProgressStats = Partial<{
  goalsPlanned: number
  milestonesDone: number
  goalsDone: number
  goalsDoneAhead: number
  checkIns: number
  streak: number
  perfectWeek: number
  comeback: number
  topHabitLevel: number
  habitsAtTree: number
  coachChats: number
  voiceCalls: number
  brainDumps: number
  following: number
  followers: number
  boostsReceived: number
  boostsGiven: number
  groves: number
  earlyCheckIns: number
  lateCheckIns: number
  weekends: number
  freezeSaves: number
  checklistDone: number
}>
export type ProgressStat = keyof ProgressStats

export type BadgeDef = {
  id: string
  tier: BadgeTier
  category: BadgeCategory
  // lucide icon name (kebab-case), like habit icons.
  icon: string
  // English fallback for server-side text (feed titles); clients use i18n.
  name: string
  stat: ProgressStat
  goal: number
}

const b = (
  id: string,
  tier: BadgeTier,
  category: BadgeCategory,
  icon: string,
  name: string,
  stat: ProgressStat,
  goal = 1,
): BadgeDef => ({ id, tier, category, icon, name, stat, goal })

export const BADGES: BadgeDef[] = [
  b('first_goal', 'seed', 'planning', 'target', 'Planted a goal', 'goalsPlanned'),
  b('first_step', 'seed', 'planning', 'footprints', 'First step', 'checkIns'),
  b('rooted', 'seed', 'planning', 'sprout', 'Rooted', 'checklistDone'),
  b('first_milestone', 'sprout', 'planning', 'flag', 'First milestone', 'milestonesDone'),
  b('milestones_10', 'sapling', 'planning', 'mountain', 'Trailblazer', 'milestonesDone', 10),
  b('goal_finished', 'sapling', 'planning', 'trophy', 'Goal reached', 'goalsDone'),
  b('goals_3', 'oak', 'planning', 'award', 'Triple harvest', 'goalsDone', 3),
  b('ahead_of_pace', 'oak', 'planning', 'rocket', 'Ahead of pace', 'goalsDoneAhead'),

  b('streak_7', 'sprout', 'habits', 'flame', 'Week of sunshine', 'streak', 7),
  b('streak_30', 'sapling', 'habits', 'flame', 'Month in bloom', 'streak', 30),
  b('streak_100', 'oak', 'habits', 'flame', 'Hundred rings', 'streak', 100),
  b('streak_365', 'ancient', 'habits', 'flame', 'Full year of growth', 'streak', 365),
  b('checkins_100', 'sprout', 'habits', 'check-check', 'Hundred check-ins', 'checkIns', 100),
  b('checkins_1000', 'oak', 'habits', 'gem', 'Thousand check-ins', 'checkIns', 1000),
  b('perfect_week', 'sapling', 'habits', 'calendar-check', 'Perfect week', 'perfectWeek'),
  b('comeback', 'sprout', 'habits', 'rotate-ccw', 'Regrowth', 'comeback'),

  b('level_sapling', 'sprout', 'growth', 'sprout', 'First sapling', 'topHabitLevel', 4),
  b('habit_grove', 'oak', 'growth', 'trees', 'Little grove', 'habitsAtTree', 3),
  b('level_oak', 'oak', 'growth', 'tree-deciduous', 'Mighty oak', 'topHabitLevel', 7),
  b('level_ancient', 'ancient', 'growth', 'tree-pine', 'Ancient tree', 'topHabitLevel', 9),

  b('first_chat', 'seed', 'coaching', 'message-circle', 'Hello, Ara', 'coachChats'),
  b('first_call', 'sprout', 'coaching', 'phone', 'Voice of the forest', 'voiceCalls'),
  b('first_brain_dump', 'seed', 'coaching', 'brain', 'Mind cleared', 'brainDumps'),
  b('brain_dumps_10', 'sapling', 'coaching', 'lightbulb', 'Clear thinker', 'brainDumps', 10),

  b('first_follow', 'seed', 'social', 'user-plus', 'New branch', 'following'),
  b('boost_received', 'seed', 'social', 'sun', 'Felt the sun', 'boostsReceived'),
  b('boosts_given_10', 'sprout', 'social', 'droplets', 'Gardener', 'boostsGiven', 10),
  b('joined_grove', 'sprout', 'social', 'users', 'Grove member', 'groves'),
  b('followers_10', 'sapling', 'social', 'sparkles', 'Shade tree', 'followers', 10),

  b('early_bird', 'sapling', 'consistency', 'sunrise', 'Early bird', 'earlyCheckIns', 10),
  b('night_owl', 'sprout', 'consistency', 'moon', 'Night owl', 'lateCheckIns', 10),
  b('weekend_warrior', 'sprout', 'consistency', 'calendar-days', 'Weekend warrior', 'weekends'),
  b('freeze_save', 'sprout', 'consistency', 'snowflake', 'Weathered the frost', 'freezeSaves'),
]

export const BADGE_BY_ID: Record<string, BadgeDef> = Object.fromEntries(
  BADGES.map((d) => [d.id, d]),
)

export const MAX_SHOWCASE = 4

export function checkBadge(def: BadgeDef, stats: ProgressStats): boolean {
  const v = stats[def.stat]
  return v !== undefined && v >= def.goal
}

// Badges newly satisfied by `stats` that the user doesn't own yet.
export function earnedBadges(stats: ProgressStats, owned: Iterable<string>): BadgeDef[] {
  const have = new Set(owned)
  return BADGES.filter((d) => !have.has(d.id) && checkBadge(d, stats))
}

// "3 / 10"-style hint for countable badges; null for one-off ones.
export function badgeProgress(def: BadgeDef, stats: ProgressStats) {
  if (def.goal <= 1) return null
  const value = Math.min(def.goal, stats[def.stat] ?? 0)
  return { value, goal: def.goal, ratio: value / def.goal }
}

const tierRank = (t: BadgeTier) => BADGE_TIERS.indexOf(t)

// Pinned badges when the user picked some, else their rarest (newest first on ties).
export function showcaseBadges(
  owned: { id: string; unlockedAt: number }[],
  pinned: string[] | undefined,
): string[] {
  const have = new Set(owned.map((o) => o.id))
  const picked = (pinned ?? []).filter((id) => have.has(id)).slice(0, MAX_SHOWCASE)
  if (picked.length) return picked
  return owned
    .filter((o) => BADGE_BY_ID[o.id])
    .sort(
      (x, y) =>
        tierRank(BADGE_BY_ID[y.id].tier) - tierRank(BADGE_BY_ID[x.id].tier) ||
        y.unlockedAt - x.unlockedAt,
    )
    .slice(0, MAX_SHOWCASE)
    .map((o) => o.id)
}

// Canopy: a leaf ring around the avatar, unlocked by badges.
export type CanopyUnlock = { badges: number } | { badge: string }

export type CanopyDef = {
  id: string
  // Leaf colours, cycled around the ring; the first is the dominant hue.
  colors: [string, string, string]
  unlock: CanopyUnlock
}

export const CANOPIES: CanopyDef[] = [
  { id: 'spring', colors: ['#6fcf4a', '#a8ec7c', '#3a9d4f'], unlock: { badges: 1 } },
  { id: 'blossom', colors: ['#ff9cc0', '#ffd1e1', '#e86a9a'], unlock: { badge: 'level_sapling' } },
  { id: 'autumn', colors: ['#f2a03d', '#e5642b', '#ffcf6e'], unlock: { badges: 10 } },
  { id: 'frost', colors: ['#8fd3ff', '#e2f4ff', '#5aa8e8'], unlock: { badge: 'streak_30' } },
  { id: 'grove', colors: ['#2e9e6b', '#7fdcae', '#17694a'], unlock: { badge: 'habit_grove' } },
  { id: 'golden', colors: ['#f5c842', '#fff0a8', '#c9921a'], unlock: { badge: 'level_ancient' } },
]

export const CANOPY_BY_ID: Record<string, CanopyDef> = Object.fromEntries(
  CANOPIES.map((c) => [c.id, c]),
)

export function canopyUnlocked(def: CanopyDef, owned: Iterable<string>): boolean {
  const have = new Set(owned)
  return 'badges' in def.unlock
    ? [...have].filter((id) => BADGE_BY_ID[id]).length >= def.unlock.badges
    : have.has(def.unlock.badge)
}

// The canopy to draw: only a known one the user still qualifies for.
export function activeCanopy(
  canopy: string | null | undefined,
  owned: Iterable<string>,
): string | null {
  const def = canopy ? CANOPY_BY_ID[canopy] : undefined
  return def && canopyUnlocked(def, owned) ? def.id : null
}

// ---- Stat helpers over the completion log (pure, unit-tested) ----

const DAY_MS = 86_400_000

const dayMs = (key: string) => Date.parse(`${key}T00:00:00Z`)

export function prevDay(key: string): string {
  return new Date(dayMs(key) - DAY_MS).toISOString().slice(0, 10)
}

// Longest run of consecutive day keys.
export function longestDayRun(keys: Iterable<string>): number {
  let best = 0
  let run = 0
  let prev = Number.NaN
  for (const k of [...new Set(keys)].sort()) {
    const ms = dayMs(k)
    run = ms - prev === DAY_MS ? run + 1 : 1
    best = Math.max(best, run)
    prev = ms
  }
  return best
}

// Came back after at least `gapDays` days without a check-in.
export function hasComeback(keys: Iterable<string>, gapDays = 7): boolean {
  const sorted = [...new Set(keys)].sort()
  for (let i = 1; i < sorted.length; i++)
    if (dayMs(sorted[i]) - dayMs(sorted[i - 1]) > gapDays * DAY_MS) return true
  return false
}

// Weekends with a check-in on both Saturday and Sunday.
export function fullWeekends(keys: Iterable<string>): number {
  const set = new Set(keys)
  let n = 0
  for (const k of set) if (new Date(dayMs(k)).getUTCDay() === 6 && set.has(nextDayKey(k))) n++
  return n
}

function nextDayKey(key: string): string {
  return new Date(dayMs(key) + DAY_MS).toISOString().slice(0, 10)
}

export function isWeekendDay(key: string): boolean {
  const d = new Date(dayMs(key)).getUTCDay()
  return d === 0 || d === 6
}

// The other half of a weekend day (Sat ↔ Sun).
export function weekendPartner(key: string): string {
  return new Date(dayMs(key)).getUTCDay() === 6 ? nextDayKey(key) : prevDay(key)
}

// Local wall-clock hour of a timestamp (tzOffsetMin as from getTimezoneOffset()).
export function localHour(ms: number, tzOffsetMin: number): number {
  return new Date(ms - tzOffsetMin * 60_000).getUTCHours()
}

// Before 8am (but after the small hours) / after 10pm.
export const isEarly = (hour: number) => hour >= 4 && hour < 8
export const isLate = (hour: number) => hour >= 22 || hour < 3
